import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../../Utils/apiClient';
import FolderBrowser from '../FolderBrowser';
import { uploadFilesToEndpoint } from '../useStorageUpload';
import {
  buildStorageReference,
  getStorageFileName,
  getStorageReferenceLabel,
} from './storageMentionUtils';

export { buildStorageReference, getStorageFileName, getStorageReferenceLabel };

const StorageMentionPicker = forwardRef(({
  selectedRefs = [],
  onSelect,
  onRemove,
  disabled = false,
  active = true,
  onClose,
  compact = false,
  showSelected = true,
  showTriggerButton = true,
  maxReferences,
  onPendingChange,
  onUploadFailure,
}, ref) => {
  const { t } = useTranslation();
  const [browserOpen, setBrowserOpen] = useState(false);
  const [storageStatus, setStorageStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const uploadPendingRef = useRef(false);
  const mountedRef = useRef(true);
  const browserOpenRef = useRef(false);
  const closePendingRef = useRef(false);
  const openVersionRef = useRef(0);
  const openPendingRef = useRef(false);
  const latestPropsRef = useRef({});
  latestPropsRef.current = { disabled, active, onSelect, onClose };
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; openVersionRef.current += 1; };
  }, []);
  const close = useCallback(() => {
    openVersionRef.current += 1;
    openPendingRef.current = false;
    setLoadingStatus(false);
    setBrowserOpen(false);
    if (browserOpenRef.current) {
      browserOpenRef.current = false;
      closePendingRef.current = true;
    }
  }, []);
  const afterClose = useCallback(() => {
    if (mountedRef.current && !browserOpenRef.current && closePendingRef.current) {
      closePendingRef.current = false;
      latestPropsRef.current.onClose?.();
    }
  }, []);
  useEffect(() => {
    if (!active || disabled) close();
  }, [active, disabled, close]);

  const attachFiles = async (incoming) => {
    const files = Array.from(incoming || []);
    if (disabled || uploadPendingRef.current || !files.length) return;
    if (maxReferences && selectedRefs.length + files.length > maxReferences) {
      setErrorMessage(t('storage_mention.limit', { count: maxReferences }));
      onUploadFailure?.(files);
      return;
    }
    uploadPendingRef.current = true;
    onPendingChange?.(true);
    setUploading(true);
    setErrorMessage('');
    try {
      const status = storageStatus || await apiRequest('/api/v1/storage/status');
      setStorageStatus(status);
      if (!mountedRef.current || latestPropsRef.current.disabled) return;
      if (!status?.path) throw new Error('Storage is unavailable');
      if (!status.upload_collision_policies?.includes('rename')) {
        setStorageStatus(null);
        setErrorMessage(t('storage_mention.upload_unavailable'));
        onUploadFailure?.(files);
        return;
      }
      const payload = await uploadFilesToEndpoint('/api/v1/storage/upload', files, {
        meta: { path: status.path, collision_policy: 'rename' },
      });
      if (mountedRef.current && !latestPropsRef.current.disabled && payload?.path) {
        (Array.isArray(payload.files) ? payload.files : []).forEach((filename) => {
          const path = `${payload.path.replace(/[\\/]+$/, '')}/${filename}`;
          latestPropsRef.current.onSelect?.(buildStorageReference(path), { replaceMentionTrigger: false, preserveText: true });
        });
      }
      if (payload?.status !== 'success' || !payload?.path || !payload?.files?.length) {
        setErrorMessage(t('storage_mention.upload_error'));
        const failedNames = new Set(Array.isArray(payload?.failed) ? payload.failed : []);
        const failedFiles = files.filter(file => failedNames.has(file.name));
        if (mountedRef.current) onUploadFailure?.(failedFiles.length ? failedFiles : files);
      }
    } catch (error) {
      if (mountedRef.current) onUploadFailure?.(files);
      setErrorMessage(t(error?.code === 'storage_quota_exceeded'
        ? 'sidebar.storage_quota_exceeded' : error?.status === 403
          ? 'storage_mention.upload_denied' : 'storage_mention.upload_error'));
    } finally {
      uploadPendingRef.current = false;
      if (mountedRef.current) {
        setUploading(false);
        onPendingChange?.(false);
      }
    }
  };

  const open = useCallback(async () => {
    if (disabled || !active || openPendingRef.current) return;
    const version = ++openVersionRef.current;
    setErrorMessage('');
    if (storageStatus) {
      browserOpenRef.current = true;
      setBrowserOpen(true);
      return;
    }
    openPendingRef.current = true;
    setLoadingStatus(true);
    try {
      const payload = await apiRequest('/api/v1/storage/status');
      if (!mountedRef.current || version !== openVersionRef.current ||
          latestPropsRef.current.disabled || !latestPropsRef.current.active) return;
      setStorageStatus(payload || {});
      browserOpenRef.current = true;
      setBrowserOpen(true);
    } catch (error) {
      if (mountedRef.current && version === openVersionRef.current) setErrorMessage(t('storage_mention.error'));
    } finally {
      if (mountedRef.current && version === openVersionRef.current) {
        openPendingRef.current = false;
        setLoadingStatus(false);
      }
    }
  }, [active, disabled, storageStatus, t]);

  useImperativeHandle(ref, () => ({ open, attachFiles, clearError: () => setErrorMessage('') }));

  const handleFileSelect = (path) => {
    if (disabled || !active || !browserOpenRef.current) return;
    if (maxReferences && selectedRefs.length >= maxReferences && !selectedRefs.some(item => item.path === path)) {
      setErrorMessage(t('storage_mention.limit', { count: maxReferences }));
      close();
      return;
    }
    const reference = buildStorageReference(path);
    if (reference.path) {
      onSelect?.(reference);
    }
    close();
  };

  return (
    <Box sx={{ mt: compact ? 0 : 1 }}>
      {uploading && <Typography variant="caption" role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <CircularProgress size={14} />{t('storage_mention.uploading')}
      </Typography>}
      {showTriggerButton && (
        <Tooltip title={t('storage_mention.button_tooltip')}>
          <span>
            <Button
              type="button"
              size={compact ? 'small' : 'medium'}
              variant="outlined"
              startIcon={loadingStatus ? <CircularProgress size={16} /> : <AttachFileIcon fontSize="small" />}
              onClick={open}
              disabled={disabled || loadingStatus}
              sx={{ textTransform: 'none', borderRadius: '8px' }}
            >
              {t('storage_mention.button')}
            </Button>
          </span>
        </Tooltip>
      )}
      {errorMessage && (
        <Typography variant="caption" color="error" role="alert" sx={{ display: 'block', mt: 0.5 }}>
          {errorMessage}
        </Typography>
      )}
      {showSelected && selectedRefs.length > 0 && (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
          {selectedRefs.map((reference) => (
            <Chip
              key={reference.path}
              size="small"
              icon={<AttachFileIcon />}
              label={reference.label || getStorageReferenceLabel(reference.path)}
              onDelete={onRemove && !disabled ? () => onRemove(reference.path) : undefined}
              sx={{ maxWidth: '100%' }}
            />
          ))}
        </Stack>
      )}
      <FolderBrowser
        open={browserOpen}
        onClose={close}
        onExited={afterClose}
        onSelect={handleFileSelect}
        restrictToUserFolder={Boolean(storageStatus?.restrict_to_user_folder ?? storageStatus?.restrictToUserFolder)}
        allowedRoots={Array.isArray(storageStatus?.allowedRoots) ? storageStatus.allowedRoots : []}
        allowFileSelection
        requireFileSelection
      />
    </Box>
  );
});

export default StorageMentionPicker;
